import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { debounce, requestYieldCallback } from '@theme/utilities';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {boolean} */
  #userHasInteracted = false;

  /** @type {Set<string>} */
  #prefetchedUrls = new Set();

  /** @type {boolean} */
  #isArrowKeyNavigation = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));
    this.addEventListener('keydown', this.#trackArrowKeyNavigation);
  }

  /**
   * Arrow-key navigation between grouped radio options fires the same native
   * `change` event as an actual click, so this flags the change as arrow-key-driven
   * to keep it from closing a hosting dialog before the user has settled on a size.
   * @param {KeyboardEvent} event
   */
  #trackArrowKeyNavigation = (event) => {
    this.#isArrowKeyNavigation = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key);
  };

  /**
   * Prefetches the product page linked by the hovered/focused card, so the
   * navigation feels instant. Debounced and deduped so repeated hovers of
   * the same link don't refetch it.
   * @param {PointerEvent | FocusEvent} event
   */
  prefetchPage = debounce((event) => {
    const link = event.target;
    if (!(link instanceof HTMLAnchorElement) || !link.href) return;
    if (this.#prefetchedUrls.has(link.href)) return;

    this.#prefetchedUrls.add(link.href);
    fetch(link.href, { priority: 'low' });
  }, 200);

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    // Mark that the user has picked the gated option and remove the requires-selection flag
    if (!this.#userHasInteracted && this.hasAttribute('data-requires-selection')) {
      // Find which option position was clicked
      const fieldset = event.target.closest('.variant-option');
      const optionPosition = fieldset instanceof HTMLElement ? fieldset.dataset.optionPosition : undefined;

      // In the standard picker every option sits in a .variant-option fieldset, and only
      // position 2 or higher completes the variant. Pickers without those fieldsets (e.g.
      // apc-size-selector) render a single gated option, so any change completes it.
      if (!fieldset || (optionPosition && parseInt(optionPosition) > 1)) {
        this.#userHasInteracted = true;
        this.removeAttribute('data-requires-selection');
      }
    }

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const wasArrowKeyNavigation = this.#isArrowKeyNavigation;
    this.#isArrowKeyNavigation = false;

    if (!wasArrowKeyNavigation) {
      const dialog = /** @type {import('@theme/dialog').DialogComponent | null} */ (this.closest('dialog-component'));
      dialog?.closeDialog();
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Reads the checked swatch's option value id from the quick-add block's product card.
   * @param {Element} quickAddBlock
   * @returns {string | null}
   */
  #cardSwatchOptionValueId(quickAddBlock) {
    const card = quickAddBlock.closest('product-card');
    const checkedSwatch = /** @type {HTMLInputElement | null} */ (
      card?.querySelector('swatches-variant-picker-component input[type="radio"]:checked') ?? null
    );
    return checkedSwatch?.dataset.optionValueId || null;
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is inside a product card context, use a dedicated section for rendering
    const quickAddBlock = this.closest('quick-add-block');
    if (quickAddBlock || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      const sectionId = quickAddBlock ? 'section-rendering-quick-add' : 'section-rendering-product-card';
      if (quickAddBlock) {
        const swatchOptionValueId = this.#cardSwatchOptionValueId(quickAddBlock);
        if (swatchOptionValueId) {
          const merged = params.length ? `${params[0]},${swatchOptionValueId}` : `option_values=${swatchOptionValueId}`;
          return `${productUrl}?section_id=${sectionId}&${merged}`;
        }
      }

      return `${productUrl}?section_id=${sectionId}&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Finds the picker in fetched HTML that corresponds to THIS picker, keyed on its block id so a
   * foreign picker (cart-reward gift, cross-sell/quick-add card, etc.) earlier in the document can't
   * be morphed in. Falls back to excluding the cart-reward picker when no block-id match exists.
   * @param {ParentNode} root - The parsed document or element to search within.
   * @returns {Element | null} The matching variant picker, or null.
   */
  #findMatchingPicker(root) {
    const tag = this.tagName.toLowerCase();
    const blockId = this.dataset.blockId;
    if (blockId) {
      const byBlock = root.querySelector(`${tag}[data-block-id="${CSS.escape(blockId)}"]`);
      if (byBlock) return byBlock;
    }
    return root.querySelector(`${tag}:not([data-cart-reward-picker])`);
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = this.#findMatchingPicker(html)?.querySelector(
          'script[type="application/json"]',
        )?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          // Skip morph in quick-add context — the rendering section doesn't have the block
          // settings needed for a full variant picker re-render, and the server-rendered
          // picker is already correct for the current product.
          let newProduct;
          if (!this.closest('quick-add-block')) {
            newProduct = this.updateVariantPicker(html);
          }

          // We grab the variant object from the response and dispatch an event with it.
          // Always dispatch for product cards (swatches-variant-picker) to ensure media updates,
          // even if no option is currently selected after morph.
          const optionId = this.selectedOptionId ?? '';
          this.dispatchEvent(
            new VariantUpdateEvent(JSON.parse(textContent), optionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            }),
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    // Match this picker by its own block id so the morph can't swap in a foreign picker (cart-reward
    // gift, cross-sell/quick-add card) that precedes it in the fetched full-page HTML.
    const newVariantPickerSource = this.#findMatchingPicker(newHtml);

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
